/* Kalve cart: window.KalveCart + the client-rendered cart drawer.
   Contract (docs/BUILD-CONTRACT.md):
     KalveCart.add(items[{id, quantity, properties?}]) -> Promise<cart>   (rejects with Error(shopify description))
     KalveCart.open() / close() / get()
     document event 'kalve:cart:updated' (detail = cart JSON) after every cart change
     [data-kalve-cart-open] opens the drawer, [data-kalve-cart-count] shows item_count. */
(function () {
  'use strict';
  if (window.KalveCart) return;

  var doc = document;
  var root = doc.documentElement;
  var GIFT_KEY = 'Gift message';
  var GIFT_DELAY = 600;

  var cart = null;          // last cart JSON
  var version = 0;          // guards against stale responses
  var queue = Promise.resolve();
  var pendingJobs = 0;
  var isOpen = false;
  var lastFocus = null;

  var data = { config: {}, strings: {} };
  var els = {};

  var pendingGift = null;   // text waiting for the debounce
  var giftTimer = null;
  var giftOn = false;
  var checkoutReady = false;

  /* upsell ("Pairs well with") */
  var CATS = ['rings', 'earrings', 'bracelets', 'necklaces'];
  /* complementarity of each category to the most recently added item's category (own category last) */
  var ORDER = {
    rings: ['bracelets', 'earrings', 'necklaces', 'rings'],
    earrings: ['bracelets', 'rings', 'necklaces', 'earrings'],
    bracelets: ['rings', 'earrings', 'necklaces', 'bracelets'],
    necklaces: ['earrings', 'bracelets', 'rings', 'necklaces']
  };
  var CAT_KEY = 'kalve:cart:cats:v1';
  var LAST_KEY = 'kalve:cart:last';
  var COLL_TTL = 5 * 60 * 1000;
  var catsPromise = null;
  var collCache = {};
  var upsellSig = '';
  var upsellBusy = '';
  var upsellToken = 0;
  var upsellData = {};
  var lastAddedVariant = Number(ssGet(LAST_KEY)) || 0;

  /* ---------- helpers ---------- */
  function route(name, fallback, suffix) {
    var r = (window.routes && window.routes[name]) || fallback;
    return r.replace(/\/$/, '') + suffix;
  }
  var urls = {
    cart: function () { return route('cart_url', '/cart', '.js'); },
    add: function () { return route('cart_add_url', '/cart/add', '.js'); },
    change: function () { return route('cart_change_url', '/cart/change', '.js'); },
    update: function () { return route('cart_update_url', '/cart/update', '.js'); },
    clear: function () { return route('cart_url', '/cart', '/clear.js'); }
  };

  function ssGet(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  function onCartPage() {
    var cartPath = ((window.routes && window.routes.cart_url) || '/cart').replace(/\/$/, '');
    return window.location.pathname.replace(/\/$/, '') === cartPath;
  }

  function text(key, fallback) { return (data.strings && data.strings[key]) || fallback || ''; }

  function request(url, body) {
    var opts = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
    if (body !== undefined) {
      opts.method = 'POST';
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    return fetch(url, opts).then(function (res) {
      return res.text().then(function (t) {
        var json = null;
        try { json = t ? JSON.parse(t) : null; } catch (e) { json = null; }
        if (!res.ok) {
          var msg = (json && (json.description || json.message)) || text('error', 'Something went wrong.');
          var err = new Error(msg);
          err.status = res.status;
          throw err;
        }
        return json;
      });
    });
  }

  function enqueue(job) {
    pendingJobs++;
    paintBusy();
    var run = queue.then(job);
    queue = run.then(noop, noop).then(function () { pendingJobs--; paintBusy(); });
    return run;
  }
  function noop() {}

  function lang() { return root.lang || undefined; }

  /* Shopify money-format placeholders ({{amount}}, {{amount_with_comma_separator}}, ...) */
  function withDelimiters(cents, precision, thousands, decimal) {
    var n = Math.round(Number(cents) || 0) / 100;
    var parts = n.toFixed(precision).split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }
  /* whole-euro amounts never show decimals ("€165", not "€165,00"); fractional ones keep two */
  function formatByTemplate(cents, template) {
    var dp = Math.round(Number(cents) || 0) % 100 === 0 ? 0 : 2;
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, kind) {
      switch (kind) {
        case 'amount': return withDelimiters(cents, dp, ',', '.');
        case 'amount_no_decimals': return withDelimiters(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(cents, dp, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(cents, dp, '\'', '.');
        case 'amount_no_decimals_with_space_separator': return withDelimiters(cents, 0, ' ', '.');
        case 'amount_with_space_separator': return withDelimiters(cents, dp, ' ', ',');
        case 'amount_with_period_and_space_separator': return withDelimiters(cents, dp, ' ', '.');
        default: return m;
      }
    });
  }

  function money(cents, currency) {
    /* the store's own money format (same as the server-rendered prices), Intl only as a fallback */
    var tpl = data.config && data.config.moneyFormat;
    if (tpl && /\{\{/.test(tpl)) return formatByTemplate(cents, tpl);
    var amount = (cents || 0) / 100;
    var whole = Math.round(cents || 0) % 100 === 0;
    var fmt = { style: 'currency', currency: currency || 'EUR' };
    if (whole) { fmt.minimumFractionDigits = 0; fmt.maximumFractionDigits = 0; }
    try {
      return new Intl.NumberFormat(lang(), fmt).format(amount);
    } catch (e) {
      try { return new Intl.NumberFormat(undefined, fmt).format(amount); }
      catch (e2) { return (whole ? String(amount) : amount.toFixed(2)) + ' ' + currency; }
    }
  }

  /* strings.<prefix>_one|few|other were rendered with sample counts 1, 2, 10; swap in the real number */
  function plural(prefix, n) {
    var samples = { one: 1, few: 2, other: 10 };
    var cat = 'other';
    try { cat = new Intl.PluralRules(lang()).select(n); } catch (e) { cat = n === 1 ? 'one' : 'other'; }
    var str = data.strings[prefix + '_' + cat];
    if (!str) { cat = 'other'; str = data.strings[prefix + '_other'] || ''; }
    return str.replace(String(samples[cat] || 10), String(n));
  }

  function sized(url, width) {
    if (!url) return '';
    try {
      var u = new URL(url, window.location.origin);
      u.searchParams.set('width', width);
      return u.toString();
    } catch (e) { return url; }
  }

  function el(tag, cls, content, attrs) {
    var node = doc.createElement(tag);
    if (cls) node.className = cls;
    if (content != null && content !== '') node.textContent = content;
    if (attrs) Object.keys(attrs).forEach(function (k) { node.setAttribute(k, attrs[k]); });
    return node;
  }

  /* ---------- DOM cache ---------- */
  function bind() {
    var blob = doc.querySelector('script[data-k-cart-data]');
    if (blob) { try { data = JSON.parse(blob.textContent); } catch (e) { /* keep defaults */ } }
    data.config = data.config || {};
    data.strings = data.strings || {};
    els = {
      drawer: doc.querySelector('[data-k-drawer]'),
      veil: doc.querySelector('[data-k-veil]'),
      items: doc.querySelector('[data-k-items]'),
      body: doc.querySelector('[data-k-body]'),
      count: doc.querySelector('[data-k-count]'),
      clear: doc.querySelector('[data-k-clear]'),
      total: doc.querySelector('[data-k-total]'),
      error: doc.querySelector('[data-k-error]'),
      gift: doc.querySelector('[data-k-gift]'),
      giftToggle: doc.querySelector('[data-k-gift-toggle]'),
      giftPanel: doc.querySelector('[data-k-gift-panel]'),
      giftText: doc.querySelector('[data-k-gift-text]'),
      checkout: doc.querySelector('[data-k-checkout]')
    };
    if (cart) { paintHeader(); paintDrawer(); }
  }

  /* ---------- state ---------- */
  function fetchCart() { return request(urls.cart()); }

  function setCart(next, announce) {
    cart = next;
    version++;
    paintHeader();
    paintDrawer();
    if (announce !== false) {
      try { doc.dispatchEvent(new CustomEvent('kalve:cart:updated', { detail: cart })); } catch (e) { /* old browsers */ }
    }
    return cart;
  }

  function refresh(announce) {
    var seen = version;
    return fetchCart().then(function (c) {
      if (version !== seen && cart) return cart; // something newer already landed
      return setCart(c, announce);
    });
  }

  /* ---------- painting ---------- */
  function paintBusy() {
    if (els.drawer) {
      if (pendingJobs > 0) els.drawer.setAttribute('aria-busy', 'true'); else els.drawer.removeAttribute('aria-busy');
    }
  }

  function paintHeader() {
    if (!cart) return;
    Array.prototype.forEach.call(doc.querySelectorAll('[data-kalve-cart-count]'), function (n) { n.textContent = cart.item_count; });
    var label = plural('bag', cart.item_count);
    if (label) {
      Array.prototype.forEach.call(doc.querySelectorAll('[data-k-bag]'), function (b) { b.setAttribute('aria-label', label); });
    }
  }

  function showError(message) {
    if (!els.error) return;
    if (message) { els.error.textContent = message; els.error.hidden = false; }
    else { els.error.textContent = ''; els.error.hidden = true; }
  }

  function paintDrawer() {
    if (!els.drawer || !cart) return;
    var n = cart.item_count;
    var empty = !cart.items.length;
    if (els.count) els.count.textContent = plural('items', n);
    if (els.clear) els.clear.hidden = empty;
    if (els.total) els.total.textContent = money(cart.total_price, cart.currency);
    var cta = els.checkout && els.checkout.querySelector('button');
    if (cta) cta.disabled = empty;

    /* remember which control had focus so a re-render does not drop it */
    var active = doc.activeElement;
    var focusKey = active && els.items && els.items.contains(active) ? active.getAttribute('data-k-focus') : null;

    if (els.items) {
      els.items.textContent = '';
      if (empty) {
        els.items.appendChild(el('p', 'k-drawer__empty', text('empty', 'Your cart is empty.')));
      } else {
        cart.items.forEach(function (item, i) { els.items.appendChild(buildItem(item, i + 1)); });
      }
    }

    if (focusKey && els.items) {
      var again = els.items.querySelector('[data-k-focus="' + focusKey.replace(/"/g, '\\"') + '"]');
      if (again) again.focus({ preventScroll: true });
      else if (els.body) els.body.focus({ preventScroll: true });
    }

    paintGift();
    renderUpsell();
  }

  function buildItem(item, line) {
    var title = item.product_title || item.title || '';
    var row = el('div', 'k-drawer__item', null, { 'data-line': line });

    var imgData = item.featured_image || (item.image ? { url: item.image, alt: '' } : null);
    var media = el('a', 'k-drawer__media', null, { href: item.url || '#', tabindex: '-1', 'aria-hidden': 'true' });
    if (imgData && imgData.url) {
      var img = doc.createElement('img');
      img.className = 'k-drawer__img';
      img.src = sized(imgData.url, 200);
      img.srcset = sized(imgData.url, 200) + ' 200w, ' + sized(imgData.url, 300) + ' 300w';
      img.sizes = '100px';
      img.width = 100;
      img.height = 125;
      img.alt = String(imgData.alt || '').replace(/\s*\[[^\]]*\]\s*$/, '') || title;
      img.loading = 'lazy';
      media.appendChild(img);
    }
    row.appendChild(media);

    var info = el('div', 'k-drawer__info');
    var r1 = el('div', 'k-drawer__row');
    var name = el('a', 'k-drawer__name', title, { href: item.url || '#' });
    r1.appendChild(name);

    var price = el('span', 'k-drawer__price');
    if (item.original_line_price > item.final_line_price) {
      var was = el('s', 'k-drawer__was', money(item.original_line_price, cart.currency));
      var wasLabel = el('span', 'k-sr', text('was_price', 'Original price') + ': ');
      price.appendChild(wasLabel);
      price.appendChild(was);
      price.appendChild(doc.createTextNode(' '));
    }
    price.appendChild(doc.createTextNode(money(item.final_line_price, cart.currency)));
    r1.appendChild(price);
    info.appendChild(r1);

    var optLabel = item.variant_title;
    if (Array.isArray(item.options_with_values) && item.options_with_values.length) {
      optLabel = item.options_with_values.map(function (o) {
        var v = String(o.value);
        return /gold|auks|rhodium|rodis|rodž|silver|sidabr/i.test(v) ? finishName(v) : v; /* old and new store values read the same */
      }).join(' / ');
    }
    if (optLabel && optLabel !== 'Default Title') {
      info.appendChild(el('p', 'k-drawer__opt', optLabel));
    }
    if (item.properties && typeof item.properties === 'object') {
      Object.keys(item.properties).forEach(function (k) {
        var v = item.properties[k];
        if (k.charAt(0) === '_' || v == null || v === '') return;
        info.appendChild(el('p', 'k-drawer__opt', k + ': ' + v));
      });
    }

    var ctl = el('div', 'k-drawer__ctl');
    var step = el('span', 'k-drawer__step');
    var minus = el('button', 'k-drawer__stepbtn', '−', {
      type: 'button', 'data-k-qty': item.quantity - 1, 'data-line': line,
      'data-k-focus': item.key + ':minus', 'aria-label': text('decrease', 'Decrease quantity')
    });
    var qty = el('span', 'k-drawer__qty', item.quantity, {
      role: 'status', 'aria-label': text('quantity_aria', '').replace('[title]', title)
    });
    var plus = el('button', 'k-drawer__stepbtn', '+', {
      type: 'button', 'data-k-qty': item.quantity + 1, 'data-line': line,
      'data-k-focus': item.key + ':plus', 'aria-label': text('increase', 'Increase quantity')
    });
    step.appendChild(minus); step.appendChild(qty); step.appendChild(plus);
    ctl.appendChild(step);
    ctl.appendChild(el('button', 'k-u k-drawer__remove', text('remove', 'Remove'), {
      type: 'button', 'data-k-qty': 0, 'data-line': line,
      'data-k-focus': item.key + ':remove', 'aria-label': text('remove_aria', '').replace('[title]', title)
    }));
    info.appendChild(ctl);
    row.appendChild(info);
    return row;
  }

  /* ---------- gift message ---------- */
  function paintGift() {
    if (!els.gift || !cart) return;
    var show = data.config.giftEnabled !== false && cart.items.length > 0;
    els.gift.hidden = !show;
    var saved = (cart.attributes && cart.attributes[GIFT_KEY]) || '';
    var typing = els.giftText && doc.activeElement === els.giftText;
    if (saved && pendingGift === null) giftOn = true;
    if (els.giftToggle) els.giftToggle.checked = giftOn;
    if (els.giftPanel) els.giftPanel.hidden = !giftOn;
    if (els.giftText && !typing && pendingGift === null && els.giftText.value !== saved) els.giftText.value = saved;
  }

  function sendGift(value) {
    var attrs = {}; attrs[GIFT_KEY] = value;
    return enqueue(function () {
      return request(urls.update(), { attributes: attrs }).then(function (c) { return setCart(c); });
    });
  }

  function flushGift() {
    clearTimeout(giftTimer);
    giftTimer = null;
    if (pendingGift === null) return queue.then(noop);
    var value = pendingGift;
    pendingGift = null;
    return sendGift(value).catch(function (err) { showError(err.message); });
  }

  function scheduleGift(value) {
    var max = Number(data.config.giftMax) || 180;
    value = String(value == null ? '' : value);
    if (value.length > max) value = value.slice(0, max);
    pendingGift = value;
    clearTimeout(giftTimer);
    giftTimer = setTimeout(flushGift, GIFT_DELAY);
  }

  /* ---------- cart actions ---------- */
  function changeLine(line, quantity) {
    showError('');
    return enqueue(function () {
      return request(urls.change(), { line: line, quantity: quantity }).then(function (c) { return setCart(c); });
    }).catch(function (err) {
      showError(err.message);
      return refresh(false).catch(noop);
    });
  }

  function clearCart() {
    showError('');
    var hadGift = cart && cart.attributes && cart.attributes[GIFT_KEY];
    clearTimeout(giftTimer); giftTimer = null; pendingGift = null; giftOn = false;
    return enqueue(function () {
      return request(urls.clear(), {}).then(function (c) {
        if (hadGift) {
          var attrs = {}; attrs[GIFT_KEY] = '';
          return request(urls.update(), { attributes: attrs });
        }
        return c;
      }).then(function (c) { return setCart(c); });
    }).catch(function (err) {
      showError(err.message);
      return refresh(false).catch(noop);
    });
  }

  /* add(items, opts): opens the drawer after a successful add (any viewport) unless opts.open === false
     or the cart page itself is showing. */
  function add(items, opts) {
    if (!Array.isArray(items)) items = [items];
    var payload = items.map(function (it) {
      var out = { id: Number(it.id), quantity: it.quantity == null ? 1 : Number(it.quantity) };
      if (it.properties && typeof it.properties === 'object') out.properties = it.properties;
      if (it.selling_plan != null) out.selling_plan = it.selling_plan;
      return out;
    });
    var shouldOpen = !(opts && opts.open === false);
    return enqueue(function () {
      return request(urls.add(), { items: payload }).then(fetchCart).then(function (c) {
        var last = payload[payload.length - 1];
        if (last && last.id) { lastAddedVariant = last.id; ssSet(LAST_KEY, String(last.id)); }
        return setCart(c);
      });
    }).then(function (c) {
      if (shouldOpen && !onCartPage()) openDrawer(false);
      return c;
    });
  }

  /* ---------- upsell: "Pairs well with" (two products from the complementary collections) ---------- */
  function rootUrl() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function fetchJson(url) {
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    });
  }

  function priceCents(v) {
    if (typeof v === 'number') return Math.round(v);           // /products/x.js and /recommendations: cents
    return Math.round(parseFloat(String(v == null ? '0' : v)) * 100) || 0; // collection products.json: "129.00"
  }

  function imgUrl(i) { return !i ? '' : (typeof i === 'string' ? i : (i.src || i.url || '')); }

  /* finish label, same rule as snippets/kalve-pdp-finish-name.liquid, for old ("925 Silver Base, Rhodium Plating") and new ("925 Silver") store values:
     silver family -> "Silver" (lt "Sidabras"), gold family -> "14K gold"; anything else keeps its text minus the word "Plating" */
  function finishName(value) {
    var v = String(value);
    var last = v.split(',').pop().trim();
    if (/gold|auks/i.test(last)) return text('finish_gold', '14K gold');
    if (/rhodium|rodis|rodž|silver|sidabr/i.test(last)) return text('finish_silver', 'Silver');
    var s = last.replace(/s+plat(ing|ed)/i, '').trim();
    return s || v;
  }

  /* option axes: a size-type option ("US ring size", "Dydis", or all-numeric values) gets a placeholder, anything else is a finish */
  function axisKind(name, values) {
    if (/size|dyd/i.test(name)) return 'size';
    if (values.length && values.every(function (v) { return /^[0-9]+([.,][0-9]+)?$/.test(String(v)); })) return 'size';
    return 'finish';
  }

  /* one product shape out of collection products.json, /products/x.js and /recommendations/products.json */
  function normProduct(p) {
    var rawOpts = p.options || [];
    var rawVariants = p.variants || [];
    var opts = rawOpts.map(function (o, i) {
      var name = typeof o === 'string' ? o : (o && o.name) || '';
      var values = o && typeof o === 'object' && Array.isArray(o.values) ? o.values.map(String) : [];
      if (!values.length) {
        rawVariants.forEach(function (v) {
          var val = Array.isArray(v.options) ? v.options[i] : v['option' + (i + 1)];
          if (val != null && val !== '' && values.indexOf(String(val)) < 0) values.push(String(val));
        });
      }
      return { index: i, name: name, values: values };
    });
    var productImg = imgUrl(p.images && p.images[0]) || imgUrl(p.featured_image) || imgUrl(p.image);
    var variants = rawVariants.map(function (v) {
      var vals = Array.isArray(v.options) ? v.options.slice() : [v.option1, v.option2, v.option3].filter(function (x) { return x != null && x !== ''; });
      vals = vals.map(String);
      var label = vals.map(function (val, i) {
        var name = (opts[i] && opts[i].name) || '';
        return finishName(val);
      }).join(' / ');
      return { id: v.id, label: label || v.title || '', vals: vals, available: v.available !== false, price: priceCents(v.price), img: imgUrl(v.featured_image) };
    });
    /* axes that need a control: every size axis, plus finish axes with more than one value ("Title: Default Title" needs none) */
    var axes = opts.filter(function (o) { return o.name !== 'Title' && o.values.length > 0; }).map(function (o) {
      o.kind = axisKind(o.name, o.values);
      return o;
    }).filter(function (o) { return o.kind === 'size' || o.values.length > 1; });
    axes.sort(function (x, y) { return (x.kind === 'size' ? 1 : 0) - (y.kind === 'size' ? 1 : 0) || x.index - y.index; }); // finish select first, size last
    return {
      id: p.id,
      handle: p.handle,
      title: p.title || '',
      img: productImg,
      variants: variants,
      axes: axes,
      available: variants.some(function (v) { return v.available; })
    };
  }

  function loadCollection(cat) {
    var hit = collCache[cat];
    if (hit && Date.now() - hit.t < COLL_TTL) return hit.p;
    var p = fetchJson(rootUrl() + 'collections/' + cat + '/products.json?limit=250').then(function (j) {
      return (j.products || []).map(normProduct);
    });
    collCache[cat] = { t: Date.now(), p: p };
    p.catch(function () { delete collCache[cat]; });
    return p;
  }

  /* handle -> category map, built once from the four collections and kept for the browser session */
  function getCategories(force) {
    if (catsPromise && !force) return catsPromise;
    if (!force) {
      var cached = null;
      try { cached = JSON.parse(ssGet(CAT_KEY) || 'null'); } catch (e) { cached = null; }
      if (cached && cached.map && cached.order) { cached.fromCache = true; catsPromise = Promise.resolve(cached); return catsPromise; }
    }
    catsPromise = Promise.all(CATS.map(loadCollection)).then(function (lists) {
      var map = {}, order = {};
      CATS.forEach(function (cat, i) {
        order[cat] = lists[i].map(function (p) { return p.handle; });
        lists[i].forEach(function (p) { if (!map[p.handle]) map[p.handle] = cat; });
      });
      var out = { map: map, order: order };
      ssSet(CAT_KEY, JSON.stringify(out));
      return out;
    }).catch(function (err) { catsPromise = null; throw err; });
    return catsPromise;
  }

  function lastCartItem() {
    if (!cart || !cart.items.length) return null;
    if (lastAddedVariant) {
      for (var i = 0; i < cart.items.length; i++) if (cart.items[i].variant_id === lastAddedVariant) return cart.items[i];
    }
    return cart.items[0]; // Shopify lists the newest line first
  }

  /* ONE code path (first render and every refresh):
     C = categories already in the cart; candidates = the four categories minus C, ordered by how well they complement the
     latest item. One available product (collection order, not in the cart) from each of the first two candidates, then the
     remaining candidates; when the cart already holds every category, the complementary categories of the latest item. */
  function pickUpsell(last) {
    var inCart = {};
    cart.items.forEach(function (it) { inCart[it.handle] = true; });

    function resolve(cats) {
      var have = {};
      cart.items.forEach(function (it) { var c = cats.map[it.handle]; if (c) have[c] = true; });
      var base = ORDER[cats.map[last.handle]] || CATS;
      var list = base.filter(function (c) { return !have[c]; }).concat(base.filter(function (c) { return have[c]; }));
      var failed = 0;
      return Promise.all(list.map(function (c) {
        return loadCollection(c).catch(function () { failed++; return []; });
      })).then(function (lists) {
        if (failed === list.length) throw new Error('collections unavailable');
        var picks = [], seen = {};
        function ok(p, cat) {
          return p.available && !inCart[p.handle] && !seen[p.handle] && (!cats.map[p.handle] || cats.map[p.handle] === cat);
        }
        function take(p) { seen[p.handle] = true; picks.push(p); }
        list.forEach(function (cat, i) { // one product per category, in order
          if (picks.length >= 2) return;
          for (var k = 0; k < lists[i].length; k++) if (ok(lists[i][k], cat)) { take(lists[i][k]); break; }
        });
        list.forEach(function (cat, i) { // still short: any further products, same order
          lists[i].forEach(function (p) { if (picks.length < 2 && ok(p, cat)) take(p); });
        });
        return picks;
      });
    }

    function unknown(cats) { return cart.items.some(function (it) { return !cats.map[it.handle]; }); }
    return getCategories().then(function (cats) {
      if (cats.fromCache && unknown(cats)) return getCategories(true).then(resolve); // product added to the shop mid-session
      return resolve(cats);
    });
  }

  function fallbackUpsell(last) {
    var inCart = {};
    cart.items.forEach(function (it) { inCart[it.handle] = true; });
    var url = rootUrl() + 'recommendations/products.json?product_id=' + encodeURIComponent(last.product_id) + '&limit=6&intent=related';
    return fetchJson(url).then(function (j) {
      return (j.products || []).map(normProduct).filter(function (p) {
        return p.available && !inCart[p.handle] && p.handle !== last.handle;
      }).slice(0, 2);
    });
  }

  function upsellBoxes() {
    return Array.prototype.slice.call(doc.querySelectorAll('[data-k-upsell]')).filter(function (b) {
      return isOpen || !(els.drawer && els.drawer.contains(b)); // the drawer copy is only worked out while the drawer is open
    });
  }

  function renderUpsell() {
    var boxes = upsellBoxes();
    if (!boxes.length || !cart) return;
    var last = lastCartItem();
    if (!last) {
      upsellToken++; upsellSig = ''; upsellBusy = '';
      boxes.forEach(function (b) { b.hidden = true; b.textContent = ''; b.removeAttribute('data-sig'); });
      return;
    }
    /* a product that just went into the cart leaves the list straight away, the next candidate follows once picked */
    var inCart = {};
    cart.items.forEach(function (it) { inCart[it.handle] = true; });
    boxes.forEach(function (b) {
      Array.prototype.forEach.call(b.querySelectorAll('.k-up__card'), function (c) {
        if (!inCart[c.getAttribute('data-handle')]) return;
        if (c.contains(doc.activeElement) && els.body) els.body.focus({ preventScroll: true });
        c.remove();
      });
    });
    var sig = last.variant_id + '|' + cart.items.map(function (it) { return it.handle; }).sort().join(',');
    var fresh = boxes.every(function (b) { return b.getAttribute('data-sig') === sig; });
    if (sig === upsellBusy || (sig === upsellSig && fresh)) return;
    upsellBusy = sig;
    var token = ++upsellToken;
    pickUpsell(last).catch(function () { return fallbackUpsell(last); }).catch(function () { return []; }).then(function (picks) {
      if (token !== upsellToken) return;
      upsellBusy = ''; upsellSig = sig;
      paintUpsell(picks, sig);
    });
  }

  function paintUpsell(picks, sig) {
    upsellBoxes().forEach(function (box) {
      var hadFocus = box.contains(doc.activeElement);
      var key = picks.map(function (p) { return p.handle; }).join(',');
      box.setAttribute('data-sig', sig);
      if (box.getAttribute('data-picks') === key && box.firstChild) return;
      box.setAttribute('data-picks', key);
      box.textContent = '';
      if (!picks.length) { box.hidden = true; return; }
      var heading = box.getAttribute('data-heading');
      if (heading) box.appendChild(el('h3', 'k-up__title', heading));
      picks.forEach(function (p) { box.appendChild(buildUpsellCard(p)); });
      box.hidden = false;
      if (hadFocus && els.body) els.body.focus({ preventScroll: true });
    });
  }

  function firstAvailable(p) {
    for (var i = 0; i < p.variants.length; i++) if (p.variants[i].available) return p.variants[i];
    return p.variants[0] || null;
  }

  function axisLabel(axis, value) { return axis.kind === 'size' ? String(value) : finishName(value); }

  /* the card's controls -> chosen value per axis index (null while the size placeholder is selected) */
  function cardChoice(card, p) {
    var choice = {};
    Array.prototype.forEach.call(card.querySelectorAll('[data-k-up-select]'), function (sel) {
      choice[sel.getAttribute('data-axis')] = sel.value === '' ? null : sel.value;
    });
    return choice;
  }

  function matches(v, choice, skip) {
    for (var k in choice) {
      if (k === skip || choice[k] == null) continue;
      if (v.vals[Number(k)] !== choice[k]) return false;
    }
    return true;
  }

  function hasNull(choice) {
    for (var k in choice) if (choice[k] == null) return true;
    return false;
  }

  /* repaints price, picture, option availability and the button for whatever is chosen right now */
  function syncCard(card) {
    var p = upsellData[card.getAttribute('data-handle')];
    if (!p) return;
    var choice = cardChoice(card, p);

    /* options that do not exist for the other choice are disabled; a size that vanished goes back to the placeholder */
    Array.prototype.forEach.call(card.querySelectorAll('[data-k-up-select]'), function (sel) {
      var idx = sel.getAttribute('data-axis');
      var axis = p.axes.filter(function (x) { return String(x.index) === idx; })[0];
      if (!axis) return;
      Array.prototype.forEach.call(sel.options, function (o) {
        if (o.value === '') return;
        var live = p.variants.some(function (v) { return v.available && v.vals[axis.index] === o.value && matches(v, choice, idx); });
        o.disabled = !live;
        o.textContent = axisLabel(axis, o.value) + (live ? '' : ' (' + text('up_sold_out', 'Sold out') + ')');
      });
      if (axis.kind === 'size' && sel.value !== '' && sel.options[sel.selectedIndex].disabled) { sel.value = ''; choice[idx] = null; }
    });

    var waiting = hasNull(choice);
    var pool = p.variants.filter(function (v) { return matches(v, choice); });
    var shown = pool.filter(function (v) { return v.available; })[0] || pool[0] || firstAvailable(p);
    var exact = waiting ? null : (pool.filter(function (v) { return v.available; })[0] || null);

    var price = card.querySelector('[data-k-up-price]');
    if (price && shown) price.textContent = money(shown.price, cart && cart.currency);
    var img = card.querySelector('.k-up__img');
    var src = (shown && shown.img) || p.img;
    if (img && src) { img.src = sized(src, 176); img.srcset = sized(src, 176) + ' 176w, ' + sized(src, 264) + ' 264w'; }

    card.setAttribute('data-variant', exact ? exact.id : '');
    var btn = card.querySelector('[data-k-up-add]');
    if (!btn || btn.hasAttribute('aria-busy')) return;
    var addAria = text('up_add_aria', '').replace('[title]', p.title) || text('up_add', 'Add');
    if (waiting) {
      btn.textContent = text('up_select_size', 'Select size');
      btn.setAttribute('aria-label', text('up_size_aria', '').replace('[title]', p.title) || text('up_select_size', 'Select size'));
      btn.setAttribute('aria-disabled', 'true');
      btn.disabled = false;
    } else {
      btn.textContent = text('up_add', 'Add');
      btn.setAttribute('aria-label', addAria);
      btn.removeAttribute('aria-disabled');
      btn.disabled = !exact;
    }
  }

  function buildUpsellCard(p) {
    var v = firstAvailable(p);
    var url = rootUrl() + 'products/' + p.handle;
    var card = el('div', 'k-up__card', null, { 'data-handle': p.handle });
    upsellData[p.handle] = p;

    var media = el('a', 'k-up__media', null, { href: url, tabindex: '-1', 'aria-hidden': 'true' });
    var src = (v && v.img) || p.img;
    if (src) {
      var img = doc.createElement('img');
      img.className = 'k-up__img';
      img.src = sized(src, 176);
      img.srcset = sized(src, 176) + ' 176w, ' + sized(src, 264) + ' 264w';
      img.sizes = '88px';
      img.width = 88; img.height = 110;
      img.alt = p.title;
      img.loading = 'lazy';
      media.appendChild(img);
    }
    card.appendChild(media);

    var info = el('div', 'k-up__info');
    var row = el('div', 'k-up__row');
    row.appendChild(el('a', 'k-up__name', p.title, { href: url }));
    row.appendChild(el('span', 'k-up__price', v ? money(v.price, cart && cart.currency) : '', { 'data-k-up-price': '' }));
    info.appendChild(row);

    /* one compact select per option: finish (first available variant preselected), then size (placeholder until chosen) */
    if (p.axes.length) {
      var wrap = el('div', 'k-up__opts');
      p.axes.forEach(function (axis) {
        var isSize = axis.kind === 'size';
        var aria = (isSize ? text('up_size_aria', '') : text('up_finish_aria', '')).replace('[title]', p.title) ||
          text('up_options_aria', '').replace('[title]', p.title);
        var sel = el('select', 'k-up__select', null, { 'data-k-up-select': '', 'data-axis': axis.index, 'aria-label': aria });
        if (isSize) {
          sel.setAttribute('data-k-up-size', '');
          var ph = el('option', null, text('up_select_size', 'Select size'), { value: '' });
          ph.disabled = true; ph.selected = true;
          sel.appendChild(ph);
        }
        axis.values.forEach(function (val) {
          var o = el('option', null, axisLabel(axis, val), { value: val });
          if (!isSize && v && v.vals[axis.index] === val) o.selected = true;
          sel.appendChild(o);
        });
        var cell = el('span', 'k-up__sel');
        cell.appendChild(sel);
        wrap.appendChild(cell);
      });
      info.appendChild(wrap);
    }

    var add = el('button', 'k-up__add', text('up_add', 'Add'), {
      type: 'button', 'data-k-up-add': '', 'aria-label': text('up_add_aria', '').replace('[title]', p.title) || text('up_add', 'Add')
    });
    if (!v || !v.available) add.disabled = true;
    info.appendChild(add);
    info.appendChild(el('p', 'k-up__err', null, { role: 'alert', hidden: '' }));
    card.appendChild(info);
    if (p.axes.length) syncCard(card); else if (v) card.setAttribute('data-variant', v.id);
    return card;
  }

  function upsellAdd(btn) {
    var card = btn.closest('.k-up__card');
    if (!card || btn.disabled) return;
    if (btn.getAttribute('aria-disabled') === 'true') { // size not chosen yet: send the shopper to the size control
      var size = card.querySelector('[data-k-up-size]');
      if (size) size.focus();
      return;
    }
    var id = Number(card.getAttribute('data-variant'));
    if (!id) return;
    var err = card.querySelector('.k-up__err');
    var box = card.closest('[data-k-upsell]');
    if (err) { err.textContent = ''; err.hidden = true; }
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = text('up_adding', 'Adding…');
    function restore() {
      if (!btn.isConnected) return;
      btn.disabled = false; btn.removeAttribute('aria-busy');
      if (card.querySelector('[data-k-up-select]')) syncCard(card); else btn.textContent = text('up_add', 'Add');
    }
    add([{ id: id, quantity: 1 }], { open: false }).then(function () {
      if (box && box.hasAttribute('data-k-up-reload')) { window.location.reload(); return; } // cart page: server-rendered lines
      setTimeout(restore, 1500);
    }).catch(function (e) {
      restore();
      if (err) { err.textContent = (e && e.message) || text('error', ''); err.hidden = false; }
    });
  }

  /* ---------- drawer open / close ---------- */
  function lock() { if (window.KalveGlobal) window.KalveGlobal.lockScroll('cart'); else root.style.overflow = 'hidden'; }
  function unlock() { if (window.KalveGlobal) window.KalveGlobal.unlockScroll('cart'); else root.style.overflow = ''; }

  /* phone top sheet sits right under the (possibly taller, announcement-bearing) header */
  function placeSheet() {
    var h = doc.querySelector('[data-k-header]');
    var wrap = h && (h.closest('.shopify-section') || h);
    if (wrap && els.drawer) {
      var bottom = Math.max(0, Math.round(wrap.getBoundingClientRect().bottom));
      els.drawer.style.setProperty('--k-drawer-top', bottom + 'px');
    }
  }

  function open() { openDrawer(true); }

  function openDrawer(refreshNow) {
    if (!els.drawer || isOpen) return;
    isOpen = true;
    lastFocus = doc.activeElement;
    showError('');
    lock();
    placeSheet();
    if (cart) paintDrawer();
    els.drawer.inert = false;
    els.drawer.removeAttribute('inert');
    els.drawer.setAttribute('aria-hidden', 'false');
    els.drawer.classList.add('k-open');
    if (els.veil) els.veil.classList.add('k-open');
    var closeBtn = els.drawer.querySelector('[data-k-drawer-close]');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
    if (refreshNow) refresh(false).catch(function (err) { showError(err.message); });
  }

  function close() {
    if (!els.drawer || !isOpen) return;
    isOpen = false;
    els.drawer.classList.remove('k-open');
    if (els.veil) els.veil.classList.remove('k-open');
    els.drawer.setAttribute('aria-hidden', 'true');
    els.drawer.inert = true;
    els.drawer.setAttribute('inert', '');
    unlock();
    flushGift();
    if (lastFocus && lastFocus.isConnected && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    lastFocus = null;
  }

  /* ---------- events (delegated) ---------- */
  doc.addEventListener('click', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;

    var opener = t.closest('[data-kalve-cart-open]');
    if (opener) {
      if (e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (!els.drawer || onCartPage()) return; // no drawer, or already on the cart page: follow href
      e.preventDefault();
      open();
      return;
    }

    var upBtn = t.closest('[data-k-up-add]');
    if (upBtn) { upsellAdd(upBtn); return; }

    if (t.closest('[data-k-drawer-close]') || t.closest('[data-k-veil]')) { close(); return; }

    var qtyBtn = t.closest('[data-k-qty]');
    if (qtyBtn && els.drawer && els.drawer.contains(qtyBtn)) {
      changeLine(Number(qtyBtn.getAttribute('data-line')), Number(qtyBtn.getAttribute('data-k-qty')));
      return;
    }

    if (t.closest('[data-k-clear]')) { clearCart(); return; }
  });

  doc.addEventListener('change', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;
    if (t.matches('[data-k-up-select]')) { var upCard = t.closest('.k-up__card'); if (upCard) syncCard(upCard); return; }
    if (!t.matches('[data-k-gift-toggle]')) return;
    giftOn = t.checked;
    if (els.giftPanel) els.giftPanel.hidden = !giftOn;
    if (giftOn) { if (els.giftText) els.giftText.focus(); }
    else {
      if (els.giftText) els.giftText.value = '';
      var had = cart && cart.attributes && cart.attributes[GIFT_KEY];
      if (had || pendingGift !== null) scheduleGift('');
    }
  });

  doc.addEventListener('input', function (e) {
    var t = e.target;
    if (t instanceof Element && t.matches('[data-k-gift-text]')) scheduleGift(t.value);
  });

  doc.addEventListener('keydown', function (e) {
    if (!isOpen || !els.drawer) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'Tab') {
      if (window.KalveGlobal) window.KalveGlobal.trapTab(els.drawer, e);
    }
  });

  /* flush the gift note before leaving for checkout */
  doc.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof Element) || !form.matches('[data-k-checkout]')) return;
    if (checkoutReady) return;
    if (pendingGift === null && pendingJobs === 0) return;
    e.preventDefault();
    var submitter = e.submitter || form.querySelector('button[name="checkout"]');
    flushGift().then(function () { return queue; }).then(function () {
      checkoutReady = true;
      if (form.requestSubmit) form.requestSubmit(submitter); else window.location.href = '/checkout';
    });
  });

  window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(false).catch(noop); });
  doc.addEventListener('shopify:section:load', function () { bind(); });
  doc.addEventListener('shopify:section:unload', function () { if (els.drawer && !els.drawer.isConnected) { if (isOpen) { isOpen = false; unlock(); } } });

  /* ---------- public API ---------- */
  window.KalveCart = {
    add: add,
    open: open,
    close: close,
    get: function () { return cart; },
    refresh: function () { return refresh(); },
    formatMoney: function (cents) { return money(cents, cart && cart.currency); }
  };

  function init() {
    bind();
    refresh(false).catch(noop);
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init); else init();
})();
